// Single source of truth for widget metadata, default size, entitlement, and
// arrangement. 4-column grid, row-height 60. `i` is the stable widget id.
//
// Do NOT bump this to roll a new default widget out: the version is part of the
// storage key, so a bump throws away every account's saved grid. New defaults
// reach existing grids through appendNewDefaults() instead.
export const DASHBOARD_VERSION = 0;

// Sections in the add-widget modal, in display order.
export const GROUPS = {
  PORTFOLIO: 'Portfolio & capacity',
  ALERTS: 'Alerts',
  CHANGE_EVENTS: 'Aggregated alerts',
  CRAWLS: 'Crawl schedules'
};
export const GROUP_ORDER = [GROUPS.PORTFOLIO, GROUPS.ALERTS, GROUPS.CHANGE_EVENTS, GROUPS.CRAWLS];

// feature_access key -> how the gate is explained to an account without it.
export const FEATURES = {
  'alert-triage': {
    name: 'AI Alert Triage',
    reason: 'Severity is assigned by AI Alert Triage, which is not enabled on your account.'
  },
  firecrawl: {
    name: 'Website Crawl',
    reason: 'Crawl scheduling is part of Website Crawl, which is not enabled on your account.'
  },
  'per-domain-monitoring': {
    name: 'Domain Monitoring',
    reason: 'Domain crawl schedules come with Domain Monitoring, which is not enabled on your account.'
  },
  'change-events': {
    name: 'Aggregated Alerts',
    reason: 'Grouped alerts and site-wide incidents come with Aggregated Alerts, which is not enabled on your account.'
  }
};

export const SUPPORT_EMAIL = 'custsupport@cloudsine.tech';

// requiresFeature: feature_access key the account needs for this widget.
export const WIDGETS = {
  portfolio: {
    group: GROUPS.PORTFOLIO,
    icon: 'desktop',
    title: 'Monitoring Portfolio',
    description: 'Your monitored URLs grouped by health status.',
    w: 1, h: 6, minW: 1, minH: 3, maxH: 8
  },
  today: {
    group: GROUPS.ALERTS,
    icon: 'bell',
    title: "Today's alerts",
    description: "Today's alert count with the change versus yesterday.",
    w: 1, h: 2, minW: 1, minH: 2, maxH: 8
  },
  blackout: {
    group: GROUPS.PORTFOLIO,
    icon: 'calendar',
    title: 'Blackout coverage',
    description: 'How many monitored URLs are currently inside an active blackout window.',
    w: 1, h: 2, minW: 1, minH: 2, maxH: 8
  },
  planUtil: {
    group: GROUPS.PORTFOLIO,
    icon: 'dashboard',
    title: 'Plan utilisation',
    description: 'URL usage against each plan tier plus your domain-monitoring limit.',
    w: 1, h: 2, minW: 1, minH: 2, maxH: 6
  },
  trend: {
    group: GROUPS.ALERTS,
    icon: 'bar-chart',
    title: 'Alert trend by severity',
    description: 'Daily alert volume split into high, medium and low severity.',
    requiresFeature: 'alert-triage',
    w: 2, h: 6, minW: 2, minH: 4, maxH: 10
  },
  topPages: {
    group: GROUPS.ALERTS,
    icon: 'warning',
    title: 'URLs with most alerts by severity',
    description: 'The URLs generating the most high, medium and low-severity alerts.',
    requiresFeature: 'alert-triage',
    w: 2, h: 6, minW: 2, minH: 4, maxH: 12
  },
  totalTrend: {
    group: GROUPS.ALERTS,
    icon: 'area-chart',
    title: 'Alert volume',
    description: 'Total alerts per day over a selectable time window.',
    w: 2, h: 6, minW: 2, minH: 4, maxH: 10
  },
  topNoisy: {
    group: GROUPS.ALERTS,
    icon: 'notification',
    title: 'URLs with most total alerts',
    description: 'The URLs generating the most alerts overall, regardless of severity.',
    w: 2, h: 6, minW: 2, minH: 4, maxH: 12
  },
  fqdnTag: {
    group: GROUPS.ALERTS,
    icon: 'cluster',
    title: 'Alerts by FQDN & tag',
    description: 'Top FQDNs and top tags by alert volume. Always account-wide — not affected by the filters.',
    w: 2, h: 6, minW: 1, minH: 4, maxH: 12
  },
  changeEventsSummary: {
    group: GROUPS.CHANGE_EVENTS,
    icon: 'pull-request',
    title: 'Aggregated alerts',
    description: 'Alerts, the changes they came from, site-wide incidents and messages, in one row.',
    requiresFeature: 'change-events',
    w: 2, h: 4, minW: 2, minH: 3, maxH: 8
  },
  openSiteIncidents: {
    group: GROUPS.CHANGE_EVENTS,
    icon: 'safety-certificate',
    title: 'Open site-wide incidents',
    description: 'Websites that are possibly defaced or unreachable right now.',
    requiresFeature: 'change-events',
    w: 2, h: 4, minW: 1, minH: 3, maxH: 10
  },
  upcomingCrawls: {
    group: GROUPS.CRAWLS,
    icon: 'clock-circle',
    title: 'Upcoming website crawls',
    description: 'The next 10 scheduled website crawls. Always account-wide — not affected by the filters.',
    requiresFeature: 'firecrawl',
    w: 2, h: 6, minW: 2, minH: 3, maxH: 10
  },
  upcomingDomainCrawls: {
    group: GROUPS.CRAWLS,
    icon: 'clock-circle',
    title: 'Upcoming domain crawls',
    description: 'The next 10 scheduled domain crawls. Always account-wide — not affected by the filters.',
    requiresFeature: 'per-domain-monitoring',
    w: 2, h: 6, minW: 2, minH: 3, maxH: 10
  }
};

// Default placement (x = column, y = row; sizes from WIDGETS). Non-triage
// accounts drop the severity widgets; vertical-compact closes the gaps.
//
// supersededBy: leave this out of the default layout when the account has that
// feature, because a richer widget already covers it. Still addable by hand.
const DEFAULT_ORDER = [
  { i: 'portfolio', x: 0, y: 0 },
  { i: 'today', x: 1, y: 0 },
  { i: 'blackout', x: 1, y: 2 },
  { i: 'planUtil', x: 1, y: 4 },
  { i: 'trend', x: 2, y: 0 },
  { i: 'topPages', x: 0, y: 6 },
  { i: 'totalTrend', x: 2, y: 6, supersededBy: 'alert-triage' },
  { i: 'topNoisy', x: 0, y: 10, supersededBy: 'alert-triage' },
  { i: 'fqdnTag', x: 2, y: 10 },
  // Entitled accounts only; isEntitled drops these for everyone else.
  { i: 'changeEventsSummary', x: 0, y: 14 },
  { i: 'openSiteIncidents', x: 2, y: 14 }
];

export function widgetLayoutItem(i, x, y) {
  const w = WIDGETS[i];
  return { i, x, y, w: w.w, h: w.h, minW: w.minW, minH: w.minH, maxH: w.maxH };
}

// `features` is the store's feature_access object. A widget with no
// requiresFeature is available to everyone.
export const isEntitled = (i, features) => {
  const required = WIDGETS[i] && WIDGETS[i].requiresFeature;
  return !required || Boolean((features || {})[required]);
};

export function buildDefaultLayout(features) {
  return DEFAULT_ORDER
    .filter((p) => WIDGETS[p.i] && isEntitled(p.i, features))
    .filter((p) => !(p.supersededBy && Boolean((features || {})[p.supersededBy])))
    .map((p) => widgetLayoutItem(p.i, p.x, p.y));
}

// The default widgets that existed before the "offered" record did. A saved
// layout with no record is treated as having been offered exactly these, so an
// account that removed one of them on purpose does not get it back, while the
// widgets added since are still appended once.
const DEFAULTS_BEFORE_OFFERED_RECORD = [
  'portfolio', 'today', 'blackout', 'planUtil', 'trend', 'topPages', 'totalTrend', 'topNoisy', 'fqdnTag'
];

/**
 * Appends to a saved layout the default widgets this account has never been
 * offered. Nothing already on the grid moves, and a widget the account was
 * offered before (present now, or present once and removed) is left alone.
 *
 * @param {object[]} layout the hydrated saved layout
 * @param {object} features the store's feature_access object
 * @param {string[]|null} offered ids recorded as already offered, or null when
 *   no record exists yet
 * @returns {{layout: object[], offered: string[]}} the layout to show and the
 *   record to persist
 */
export function appendNewDefaults(layout, features, offered) {
  const seen = new Set(Array.isArray(offered) ? offered : DEFAULTS_BEFORE_OFFERED_RECORD);
  layout.forEach((w) => seen.add(w.i));
  const defaults = buildDefaultLayout(features);
  const additions = defaults.filter((d) => !seen.has(d.i));
  // Below everything already placed, keeping the defaults' relative rows.
  const bottom = layout.reduce((m, w) => Math.max(m, w.y + w.h), 0);
  const top = additions.reduce((m, d) => Math.min(m, d.y), Infinity);
  const next = layout.concat(additions.map((d) => ({ ...d, y: bottom + (d.y - top) })));
  defaults.forEach((d) => seen.add(d.i));
  return { layout: next, offered: Array.from(seen) };
}

// Drop unknown/unentitled widgets and re-attach size constraints from the
// registry (only geometry is persisted).
export function hydrateSavedLayout(saved, features) {
  return (Array.isArray(saved) ? saved : [])
    .filter((s) => s && WIDGETS[s.i] && isEntitled(s.i, features))
    .map((s) => {
      const w = WIDGETS[s.i];
      return {
        i: s.i,
        x: Number(s.x),
        y: Number(s.y),
        w: Number(s.w),
        h: Number(s.h),
        minW: w.minW,
        minH: w.minH,
        maxH: w.maxH
      };
    });
}
